/**
 * `/history` — every call this app has ever created.
 *
 * It reads SQLite through `GET /api/history`, NOT `GET /v1/sessions`, and that
 * is the whole reason the local catalog exists:
 *
 *   - the upstream listing keysets on `created_at` and is not a change feed, so
 *     finding this browser's six rooms would mean paging the whole world;
 *   - retention eventually expires the artifacts and then the session, and a
 *     call that has been purged still happened and still belongs on this list;
 *   - the join code, the display names and "who created this" are concepts
 *     era-control has no model for, because they are not its problem.
 *
 * The one thing the catalog cannot answer is a session this app did not create
 * — the reference load-test sessions, for instance. Paste one of those ids into
 * the search box and the screen offers to open it directly, because the detail
 * screen works for any session id the control plane knows.
 *
 * ON A PHONE this is the one screen in the app where something scrolls, and it
 * is deliberately the LIST and not the page. The header, the totals, the search
 * box and the filters are a fixed frame; the rows move underneath it. That is
 * the difference between a list you can filter and a list you have to scroll
 * back up to filter — and it is why the root is sized to exactly one viewport
 * (the same calc the lobby uses) with the list as the only `overflow-y` in it.
 *
 * `overscroll-contain` on that region matters more than it looks: without it,
 * flicking past the end of the list chains the scroll up into the shell's own
 * scroller and the whole frame slides, which is the single most website-like
 * thing a web app can do on iOS.
 *
 * At `sm` and up none of this applies — the root is the original document flow,
 * the list has no scroller of its own, and the page scrolls as it always did.
 */

import * as React from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRight, Hourglass, Loader2, Search, Sparkles, X } from 'lucide-react'
import type { RoomSummary } from '@era/shared'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDuration } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useHistory } from '@/features/history/api'
import { SessionRow } from '@/features/history/components/session-row'
import {
  groupByDay,
  looksLikeSessionId,
  matchesQuery,
  matchesStatus,
  STATUS_FILTERS,
  totals,
  type StatusFilter,
} from '@/features/history/filter'

/**
 * Below the `sm` breakpoint, as a value rather than as a class.
 *
 * Used for exactly one thing — the search placeholder, which CSS cannot
 * shorten. Kept as a media query on WIDTH rather than `pointer: coarse`
 * because it is a question about how many characters fit, not about what is
 * pointing at them; `639.98px` is Tailwind's own `sm` boundary. Anything that
 * CAN be done with a `sm:` class is done with one.
 */
const PHONE_QUERY = '(max-width: 639.98px)'

function useIsPhone(): boolean {
  return React.useSyncExternalStore(
    (onChange) => {
      const media = window.matchMedia(PHONE_QUERY)
      media.addEventListener('change', onChange)
      return () => media.removeEventListener('change', onChange)
    },
    () => window.matchMedia(PHONE_QUERY).matches,
    () => false,
  )
}

export function HistoryRoute() {
  const history = useHistory()
  const isPhone = useIsPhone()
  const [query, setQuery] = React.useState('')
  const [status, setStatus] = React.useState<StatusFilter>('all')

  const rooms = history.data?.rooms ?? []
  const needle = query.trim().toLowerCase()

  const filtered = React.useMemo(
    () => rooms.filter((room) => matchesStatus(room, status) && matchesQuery(room, needle)),
    [rooms, status, needle],
  )
  const groups = React.useMemo(() => groupByDay(filtered), [filtered])
  const summary = React.useMemo(() => totals(rooms), [rooms])

  // Somebody has pasted a session id this app has never seen. The detail screen
  // handles it fine — it is served live from the control plane — so offer it
  // rather than reporting "no results" for a session that plainly exists.
  const foreignId =
    looksLikeSessionId(query) && !rooms.some((room) => room.session_id === query.trim())
      ? query.trim()
      : null

  const refreshingPill =
    history.data && history.data.refreshing > 0 ? (
      <span
        className="inline-flex shrink-0 items-center gap-2 rounded-full border border-line bg-surface-2 px-3 py-1 text-xs text-ink-muted"
        title={`${history.data.refreshing} unfinished ${history.data.refreshing === 1 ? 'session remains' : 'sessions remain'} inside the bounded reconciliation window; this is not a concurrent worker count`}
      >
        <Hourglass className="size-3" />
        {history.data.refreshing} unfinished
      </span>
    ) : null

  return (
    <div
      className={cn(
        /* The same one-viewport frame the lobby uses, and for the same reason:
           the shell's chrome subtracted by token name rather than by a guess.
           Here it does one extra job — it is what gives the list below a
           definite height to be `flex-1` of, and without a definite height
           `overflow-y-auto` on that region would size to its content and never
           scroll at all. */
        'flex h-[calc(100dvh_-_var(--sat)_-_var(--sab)_-_var(--tabbar-h)_-_3rem)] flex-col gap-2.5',
        'sm:block sm:h-auto sm:space-y-8',
      )}
    >
      <div className="flex shrink-0 items-start justify-between gap-3 sm:hidden">
        <h1 className="display min-w-0 text-2xl leading-tight text-ink">
          Everything you have recorded.
        </h1>
        {refreshingPill}
      </div>

      <div className="hidden sm:block">
        <PageHeader
          title="Everything you have recorded."
          lede="Kept locally, so a call is still listed here after the platform's retention window has closed over its audio."
          actions={refreshingPill}
        />
      </div>

      {rooms.length > 0 ? (
        /* Three tiles at every width, tighter below `sm`. Not dropped on a
           phone and not collapsed into a sentence: this is the only place in
           the app that answers "how much have I actually got in here", and it
           is three numbers, which is exactly what a phone can hold in a row.
           The labels shorten instead — "transcripts ready" wraps to two lines
           in a 109px column and then the tile is taller than the tile beside
           it, which reads as a layout bug rather than a long word. */
        <div className="grid shrink-0 grid-cols-3 gap-2 sm:gap-3">
          <Stat label="calls" value={String(summary.calls)} />
          <Stat label="recorded" short="kept" value={formatDuration(summary.recordedMs)} />
          <Stat
            label="transcripts ready"
            short="ready"
            value={`${summary.transcripts}`}
            hint={summary.processing > 0 ? `${summary.processing} still processing` : undefined}
          />
        </div>
      ) : null}

      {/* -- toolbar ---------------------------------------------------------- */}
      <div className="shrink-0 space-y-2 sm:space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-ink-dim" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            // A placeholder cannot be shortened in CSS, and this one is 57
            // characters in a 343px box — it does not ellipsize, it is simply
            // cut, and "Search by title, person, join cod" reads as a broken
            // control rather than a helpful one. Hence the media query in JS
            // for what is otherwise a styling decision. The session-id trick is
            // unchanged and still works; it is just no longer advertised on the
            // screen where nobody is pasting a session id.
            placeholder={
              isPhone
                ? 'Search your calls'
                : 'Search by title, person, join code — or paste a session id'
            }
            className="h-11 pl-10"
            aria-label="Search your calls"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              // 44x44 against the 32px it was. The icon is unchanged; only the
              // target around it grew, and the negative inset keeps it looking
              // exactly where it looked before.
              className="absolute top-1/2 right-1 flex size-11 -translate-y-1/2 items-center justify-center rounded-full text-ink-dim hover:text-ink sm:right-3 sm:size-8"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>

        {/* One line, always. Five filters do not fit across 343px and wrapping
            them costs two more rows of a viewport this screen is trying to fit
            inside; a horizontally scrolling chip row is what iOS does with the
            same problem. It bleeds to the screen edges (`-mx-4` against the
            shell's `px-4`) so that a chip cut off at the edge reads as "there
            is more this way" rather than as a clipped layout. */}
        <div
          className={cn(
            // `-my-1 py-1` is a hit-area concession, not spacing. The chips are
            // 37.5px tall and `.tap44` centres a 44px touch box on each, which
            // needs 3.25px of room above and below — and this row scrolls, so
            // `overflow-x: auto` computes `overflow-y` to `auto` and CLIPS that
            // box away rather than merely hiding it. The padding supplies the
            // room; the equal negative margin gives it straight back to the
            // layout, so the row occupies exactly the height it did before.
            '-mx-4 -my-1 flex flex-nowrap items-center gap-1.5 overflow-x-auto px-4 py-1',
            '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            'sm:mx-0 sm:my-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:py-0',
          )}
        >
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setStatus(option.value)}
              aria-pressed={status === option.value}
              className={cn(
                'tap44 shrink-0 rounded-full border px-3 py-2 text-[0.8125rem] font-medium whitespace-nowrap transition-colors sm:py-1',
                status === option.value
                  ? 'border-accent-line bg-accent-soft text-accent'
                  : 'border-line bg-surface text-ink-muted hover:text-ink active:bg-surface-2',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* -- the list, and the ONE thing on this screen that scrolls ----------
          `min-h-0` is load-bearing and is the classic omission: a flex child's
          default `min-height: auto` refuses to shrink below its content, so
          without it this box grows to the height of every row in the catalog,
          pushes the frame off the bottom of the screen, and scrolls nothing.

          The horizontal bleed puts the scroll gutter at the screen edge and,
          more importantly, stops `overflow-y` (which computes overflow-x to
          `auto` alongside it) from clipping the rows' own shadows. */}
      <div
        className={cn(
          '-mx-4 min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-2',
          'sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0',
        )}
      >
        {foreignId ? <ForeignSession sessionId={foreignId} /> : null}

      {history.isPending ? (
        <div className="space-y-2.5">
          {[0, 1, 2].map((row) => (
            <Card key={row} className="flex items-center gap-3 px-3 py-4 sm:gap-4 sm:px-5">
              <Skeleton className="size-7 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3.5 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-6 w-20 rounded-full" />
            </Card>
          ))}
        </div>
      ) : history.isError ? (
        // The message alone is a dead end — "the era-meet server is not
        // responding" tells you what broke and nothing about what to do. This
        // list is served from SQLite by THIS app's own process, so the failure
        // is local and the recovery is local; say which, and offer the retry.
        <Card className="border-critical/30 bg-critical-soft/40 px-5 py-8 text-center sm:px-6 sm:py-10">
          <p className="text-sm font-semibold text-critical">The catalog could not be read</p>
          <p className="mx-auto mt-2 max-w-md text-[0.8125rem] leading-relaxed text-ink-muted">
            {history.error.status === 0 ? (
              <>
                This page is served by the same process that holds the catalog, so if the page
                loaded and this did not, that process has stopped since. Restart it with{' '}
                <code className="ident rounded bg-surface-3 px-1 py-0.5 text-[0.75rem]">
                  pnpm start
                </code>{' '}
                and try again — nothing has been lost, the catalog is a file on disk.
              </>
            ) : (
              history.error.message
            )}
          </p>
          <Button
            variant="outline"
            size="sm"
            // The only way out of the error state, so it is the one control on
            // the screen that must not be missable. `size="sm"` is 34px tall;
            // `tap44` takes the touch box to 44 and leaves the button's own
            // size alone, so the error card keeps its proportions.
            className="tap44 mt-5"
            onClick={() => void history.refetch()}
            disabled={history.isFetching}
          >
            {history.isFetching ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                Retrying
              </>
            ) : (
              'Try again'
            )}
          </Button>
        </Card>
      ) : rooms.length === 0 ? (
        <EmptyCatalog />
      ) : filtered.length === 0 ? (
        <NoMatches onClear={() => { setQuery(''); setStatus('all') }} />
      ) : (
        <div className="space-y-5 sm:space-y-8">
          {groups.map((group) => (
            <section key={group.key} className="space-y-2.5">
              {/* The day heading stays with its rows inside the scroller
                  rather than sticking to the top of it. A sticky heading is
                  the right pattern for a list of hundreds; this catalog is one
                  browser's own calls, and a sticky bar over four rows is
                  chrome pretending to be structure. */}
              <div className="flex items-baseline gap-3">
                <h2 className="eyebrow">{group.label}</h2>
                <span className="h-px flex-1 bg-line" />
                <span className="text-[0.6875rem] text-ink-dim">
                  {group.rooms.length} {group.rooms.length === 1 ? 'call' : 'calls'}
                </span>
              </div>
              <div className="space-y-2">
                {group.rooms.map((room: RoomSummary) => (
                  <SessionRow key={room.session_id} room={room} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

/**
 * One number and what it counts.
 *
 * `short` is the phone label. A 109px column at 11px letter-spaced uppercase
 * holds about eight characters, and "transcripts ready" is seventeen — so the
 * choice is a two-line label (which makes one tile taller than the two beside
 * it) or a shorter word. `hint` goes the same way: "2 still processing" is a
 * third line on a tile that has room for two, and the "Processing" filter chip
 * two rows below says the same thing and can be tapped.
 */
function Stat({
  label,
  short,
  value,
  hint,
}: {
  label: string
  short?: string
  value: string
  hint?: string
}) {
  return (
    <div className="rounded-xl border border-line bg-surface px-3 py-2.5 sm:px-5 sm:py-4">
      <p className="eyebrow truncate">
        <span className={short ? 'sm:hidden' : undefined}>{short ?? label}</span>
        {short ? <span className="hidden sm:inline">{label}</span> : null}
      </p>
      <p className="mt-1.5 text-lg leading-none font-semibold text-ink sm:text-2xl">{value}</p>
      {hint ? <p className="mt-1.5 hidden text-[0.6875rem] text-ink-dim sm:block">{hint}</p> : null}
    </div>
  )
}

function ForeignSession({ sessionId }: { sessionId: string }) {
  return (
    // `mb-*` rather than the root's `space-y`: this card now lives inside the
    // scrolling region with the list, so the gap that used to come from the
    // page's own rhythm has to be carried here. 8 at `sm` and up is exactly the
    // `space-y-8` it replaced.
    <Card className="mb-3 flex flex-wrap items-center gap-3 border-accent-line/60 bg-accent-soft/40 px-4 py-4 sm:mb-8 sm:gap-4 sm:px-5">
      <div className="min-w-0 flex-1">
        <p className="text-[0.8125rem] font-medium text-ink">
          That is a session id this app did not create.
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">
          It can still be opened — the detail screen reads any session the control plane knows. It
          will have no join code and no display names, because those only ever existed here.
        </p>
      </div>
      <Button variant="primary" size="sm" asChild>
        <Link to="/session/$sessionId" params={{ sessionId }}>
          Open it
          <ArrowRight className="size-3.5" />
        </Link>
      </Button>
    </Card>
  )
}

function EmptyCatalog() {
  return (
    <Card className="border-dashed px-5 py-10 text-center sm:px-6 sm:py-14">
      <p className="display text-xl text-ink">No calls yet.</p>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink-muted">
        Start one and it appears here the moment it is created — while it is still running, and
        again with its transcript a few minutes after it ends.
      </p>
      <Button variant="primary" asChild className="mt-6">
        <Link to="/">
          <Sparkles className="size-4" />
          Start a call
        </Link>
      </Button>
    </Card>
  )
}

function NoMatches({ onClear }: { onClear: () => void }) {
  return (
    <Card className="border-dashed px-5 py-9 text-center sm:px-6 sm:py-12">
      <p className="text-sm font-medium text-ink">Nothing matches.</p>
      <button
        type="button"
        onClick={onClear}
        className="tap44 mt-2 text-[0.8125rem] font-medium text-accent hover:underline"
      >
        Clear the search and filters
      </button>
    </Card>
  )
}
